import {
  ArrowUpward,
  Mic,
  MicOff,
  Refresh,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import React from 'react';

export interface ChatComposerProps {
  disabled: boolean;
  inputMessage: string;
  isListening: boolean;
  ttsEnabled: boolean;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onToggleListening?: () => void;
  onToggleTts?: () => void;
  onStartNewChat?: () => void;
}

export default function ChatComposer(
  props: ChatComposerProps,
): React.ReactElement {
  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2,
        bgcolor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          width: '100%',
          bgcolor: 'rgba(255, 213, 0, 0.65)',
          border: '1px solid #FFD400',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          borderRadius: '16px',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type here..."
          value={props.inputMessage}
          onChange={(e) => props.onChange(e.target.value)}
          onKeyPress={props.onKeyPress}
          disabled={props.disabled}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontFamily:
                'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontSize: 16,
              lineHeight: 1.4,
              color: '#000000',
              '& ::placeholder': {
                color: '#828282',
                opacity: 1,
              },
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {props.onToggleTts && (
              <IconButton
                aria-label="Toggle voice output"
                onClick={props.onToggleTts}
                disabled={props.disabled}
                size="small"
              >
                {props.ttsEnabled ? (
                  <VolumeUp sx={{ color: '#828282' }} />
                ) : (
                  <VolumeOff sx={{ color: '#828282' }} />
                )}
              </IconButton>
            )}
            {props.onToggleListening && (
              <IconButton
                aria-label="Voice input"
                onClick={props.onToggleListening}
                disabled={props.disabled}
                size="small"
              >
                {props.isListening ? (
                  <MicOff sx={{ color: '#828282' }} />
                ) : (
                  <Mic sx={{ color: '#828282' }} />
                )}
              </IconButton>
            )}
            {props.onStartNewChat && (
              <IconButton
                aria-label="Start new chat"
                onClick={() => {
                  if (window.confirm('Start a new conversation? This will end your current chat.')) {
                    props.onStartNewChat?.();
                  }
                }}
                size="small"
              >
                <Refresh sx={{ color: '#828282' }} />
              </IconButton>
            )}
          </Box>

          <IconButton
            aria-label="Send"
            onClick={props.onSend}
            disabled={props.disabled || !props.inputMessage.trim()}
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#FFFFFF',
              border: '1px solid #FFFFFF',
              '&:hover': {
                bgcolor: '#F0F0F0',
              },
              '&.Mui-disabled': {
                bgcolor: '#FFFFFF',
                border: '1px solid #FFFFFF',
              },
            }}
          >
            <ArrowUpward sx={{ color: '#FFD400' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
